/**
 * 预算卡（**G 域**）—— 总预算 / 分类预算 / 事件预算**同一个组件**（服务端下发同一形状）
 *
 * 依据：G 域 PRD **§4.5**（派生指标与呈现）· **§4.4**（待缴标注）· `AC-G1.8` ~ `AC-G1.14` ·
 * `AC-G1.30`（三态配色）· 铁律 8（**琥珀 = 接近 · 朱红 = 已超**）· `DR-036`（金额必带币种）。
 *
 * ## 🔴 本组件**一个数都不算**
 *
 * | 显示的东西 | 来源 |
 * |---|---|
 * | 已用 / 总额 / 剩余 / 已用% / 时间进度 / 偏离 / 外推 | 服务端的 `progress.*`（`AC-G1.8` ~ `G1.11`） |
 * | 档位标签与配色 | 服务端的 `level`（`AC-G1.30`） |
 * | 「含待缴」金额 | 服务端的 `progress.unpaidPending`（`AC-G1.13`） |
 * | 超支金额 | 服务端的 `progress.remaining` 取绝对（`AC-G1.8`） |
 *
 * ⇒ 本文件里**没有**除法、没有 `reduce`、没有 `new Date()`。
 *
 * ## ⚠️ 刻意**不用 `Portal`**（㉝ 批的纪律）
 * 本卡片需要被 `renderToStaticMarkup` 断言 ⇒ 一切渲染都在普通容器里
 * （MUI `Dialog`/`Popover` 走 Portal，静态渲染下内容是否进产物取决于实现细节）。
 */

import { Box, Button, Chip, Stack, Switch, Typography } from '@mui/material';
import {
  BudgetPeriod,
  divideExact,
  type BudgetCardView,
  type BudgetProgressView,
} from '@jiaying/shared';
import { Amount } from './Amount.js';
import {
  EVENT_STATE_LABELS,
  LEVEL_COLORS,
  LEVEL_LABELS,
  PERIOD_LABELS,
  deviationText,
  overspentAmount,
  percentText,
  unpaidAmount,
} from '../features/budget-labels.js';

export interface BudgetCardProps {
  card: BudgetCardView;
  /**
   * 启停（`AC-G1.26`：停用不进预警、不入快照）。
   *
   * 🔴 **可选** ⇒ 这就是**只读变体**：不传时**不渲染那个开关**（而不是渲染一个点了不动的控件）。
   * 需求来自 K 域 `AC-K3.6`（报表③段与预算页**同一组件**，不复制实现）——
   * 报表页是**只读呈现**，它没有任何理由去"停用某个预算"。
   */
  onToggle?: (enabled: boolean) => void;
  /**
   * 下钻待缴明细（`AC-G1.14`：跳流水页 + 按「待缴·未付」筛选）。
   *
   * 🔴 **可选** —— 不传时「含待缴 ¥X 未付」**仍然显示**（那是**信息**，`AC-G1.13` 要求），
   * 只是**不是按钮**（不可点）。*信息*与*控件*必须分开取舍：删信息是丢功能，删控件只是去掉动作。
   */
  onDrillUnpaid?: () => void;
  /**
   * 🆕 调整额度/周期（批次 `95` · 负责人「预算没有调整入口」）。
   * 🔴 可选 ⇒ 与 `onToggle` 同一约定：不传就**不渲染按钮**（报表页是只读变体）。
   */
  onEdit?: () => void;
}

export function BudgetCard({ card, onToggle, onDrillUnpaid, onEdit }: BudgetCardProps) {
  const p = card.progress;
  const levelLabel = LEVEL_LABELS[card.level];
  const color = LEVEL_COLORS[card.level];
  const over = overspentAmount(p);
  const unpaid = unpaidAmount(p);
  const isEvent = card.period === BudgetPeriod.Event;

  return (
    <Box
      data-testid={`budget-card-${card.id}`}
      data-level={card.level}
      data-total={card.isTotal ? '1' : '0'}
      data-enabled={card.enabled ? '1' : '0'}
      sx={{
        border: 1,
        borderColor: card.level === 'over' ? 'error.main' : 'divider',
        borderRadius: 1,
        p: 1.5,
        opacity: card.enabled ? 1 : 0.6,
      }}
    >
      {/* ── 头部：名称 + 周期 + 档位 ── */}
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {card.categoryName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {PERIOD_LABELS[card.period]}
            {isEvent && card.startDate !== null && card.endDate !== null
              ? ` · ${card.startDate} – ${card.endDate}`
              : ''}
            {isEvent && card.eventState !== null ? ` · ${EVENT_STATE_LABELS[card.eventState]}` : ''}
            {card.isTotal ? ' · 窗口内全部支出合计' : ''}
          </Typography>
        </Box>
        {levelLabel === null ? null : (
          <Chip size="small" color={color} label={levelLabel} data-testid={`level-${card.id}`} />
        )}
        {onEdit === undefined ? null : (
          <Button size="small" onClick={onEdit} data-testid={`edit-${card.id}`}>
            调整
          </Button>
        )}
        {/* 启停（停用后不再生成预警，也不参与软提示合计）
            🔴 **只读变体（`onToggle` 缺省）⇒ 整个开关不渲染**（不画点不动的控件） */}
        {onToggle === undefined ? null : (
          <Switch
            size="small"
            checked={card.enabled}
            onChange={(e) => {
              onToggle(e.target.checked);
            }}
            data-testid={`toggle-${card.id}`}
            title={card.enabled ? '停用后不进预警、不入判档快照（已产生的流水不受影响）' : '启用'}
          />
        )}
      </Stack>

      {/* ── 金额 ── */}
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline', mt: 1, flexWrap: 'wrap' }}>
        <Amount cents={p.spent} currency={card.currency} variant="h5" colored={false} />
        <Typography variant="body2" color="text.secondary">
          /
        </Typography>
        <Amount cents={card.amount} currency={card.currency} variant="body1" colored={false} />
      </Stack>

      {/* ── 进度条（🔴 填充色三态由 `level` 决定；时间进度刻度线只在月度显示）── */}
      <Box sx={{ position: 'relative', mt: 1, height: 8, borderRadius: 4, bgcolor: 'action.hover' }}>
        <Box
          data-testid={`bar-${card.id}`}
          sx={{
            width: `${clampPercent(p)}%`,
            height: '100%',
            borderRadius: 4,
            bgcolor: `${color}.main`,
          }}
        />
        {isEvent ? null : (
          <Box
            data-testid={`mark-${card.id}`}
            sx={{
              position: 'absolute',
              top: -2,
              left: `${clampPercentOf(p.timeProgressE4)}%`,
              width: '2px',
              height: 12,
              bgcolor: 'text.secondary',
            }}
          />
        )}
      </Box>

      {/* ── 派生指标（全部来自服务端）── */}
      <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="caption" data-testid={`used-${card.id}`}>
          已用 {percentText(p.usedE4)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          剩余{' '}
          <Amount cents={p.remaining} currency={card.currency} variant="caption" colored={false} />
        </Typography>
        {isEvent ? null : (
          <Typography variant="caption" color="text.secondary" data-testid={`deviation-${card.id}`}>
            {deviationText(p.deviationE4)}
          </Typography>
        )}
      </Stack>

      {/* 🔴 外推：`null` ⇒ **不显示**（`AC-G1.10` 的"月初不外推"）—— 不是显示 0 */}
      {p.projectedEnd === null ? null : (
        <Typography variant="caption" color="text.secondary" data-testid={`projected-${card.id}`}>
          按当前节奏，本期预计支出{' '}
          <Amount cents={p.projectedEnd} currency={card.currency} variant="caption" colored={false} />
        </Typography>
      )}

      {/* ── 超支：给下一步动作，不做指责（`AC-G1.8` / 配色纪律）── */}
      {over === null ? null : (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block' }} data-testid={`over-${card.id}`}>
          超出{' '}
          <Amount
            cents={over as BudgetProgressView['remaining']}
            currency={card.currency}
            variant="caption"
            colored={false}
          />
        </Typography>
      )}

      {/* ── 待缴标注：🔴 仅 `> 0` 显示；点击下钻按「待缴·未付」筛选（`AC-G1.13` / `G1.14`）──
          🔴 **只读变体（`onDrillUnpaid` 缺省）⇒ 仍显示金额，但不是按钮**（信息不丢，动作去掉） */}
      {unpaid === null ? null : onDrillUnpaid === undefined ? (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5 }}
          data-testid={`unpaid-${card.id}`}
        >
          含待缴{' '}
          <Amount
            cents={unpaid as BudgetProgressView['unpaidPending']}
            currency={card.currency}
            variant="caption"
            colored={false}
          />{' '}
          未付
        </Typography>
      ) : (
        <Box
          component="button"
          onClick={onDrillUnpaid}
          data-testid={`unpaid-${card.id}`}
          sx={{
            mt: 0.5,
            border: 'none',
            background: 'none',
            p: 0,
            cursor: 'pointer',
            color: 'primary.main',
            font: 'inherit',
            fontSize: '0.75rem',
          }}
        >
          含待缴{' '}
          <Amount
            cents={unpaid as BudgetProgressView['unpaidPending']}
            currency={card.currency}
            variant="caption"
            colored={false}
          />{' '}
          未付
        </Box>
      )}
    </Box>
  );
}

/** 进度条填充宽度（**只做展示裁剪**：超支时填满，不做数值口径） */
function clampPercent(p: BudgetProgressView): number {
  return clampPercentOf(p.usedE4);
}

function clampPercentOf(e4: number): number {
  if (e4 <= 0) return 0;
  if (e4 >= 10000) return 100;
  // 🔴 只用于 CSS 宽度：走共享白名单的整数除法（端侧禁 `/`，见 eslint 的定点规则）
  return divideExact(e4, 100);
}
